import * as React from "react";
import { useTranslation } from "react-i18next";
import { UserStatus } from "@dataconnect/generated";
import { useGetUserCredits } from "@dataconnect/generated/react";
import { XCircleIcon } from "@/src/components/icons";
import { auth, functions } from "@/src/config/firebase";
import { httpsCallable } from "firebase/functions";

interface OutOfCreditsOverlayProps {
  onClose: () => void;
}

const SUPPORT_EMAIL = "hello@getshowcase.ai";

export const OutOfCreditsOverlay: React.FC<OutOfCreditsOverlayProps> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const [customerSessionClientSecret, setCustomerSessionClientSecret] =
    React.useState<string | null>(null);

  const { data } = useGetUserCredits({ enabled: !!auth.currentUser });
  const userData = data?.user;

  // Fetch Stripe customer session for pricing table
  React.useEffect(() => {
    if (userData?.stripeCustomerId) {
      const fetchClientSecret = async () => {
        try {
          const createStripeCustomerSession = httpsCallable(
            functions,
            "createStripeCustomerSession",
            { timeout: 150_000 }
          );
          const { data: result } = await createStripeCustomerSession({});
          if (
            result &&
            typeof result === "object" &&
            "clientSecret" in result
          ) {
            setCustomerSessionClientSecret(
              (result as { clientSecret: string }).clientSecret
            );
          }
        } catch (error) {
          console.error("Failed to create Stripe customer session:", error);
        }
      };
      fetchClientSecret();
    }
  }, [userData?.stripeCustomerId]);

  // Load Stripe pricing table script for users who can (re-)subscribe
  React.useEffect(() => {
    if (
      userData?.status === UserStatus.TRIAL ||
      userData?.status === UserStatus.CANCELED
    ) {
      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/pricing-table.js";
      script.async = true;
      document.body.appendChild(script);
      return () => {
        if (script.parentNode) document.body.removeChild(script);
      };
    }
  }, [userData?.status]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto flex flex-col animate-in zoom-in-95 fade-in duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-700 transition-colors p-2 hover:bg-gray-100 rounded-full"
          aria-label={t("common.close")}
        >
          <XCircleIcon className="w-6 h-6" />
        </button>

        <div className="p-8 pb-6">
          <h2 className="text-xl tracking-tight font-semibold text-gray-900 pr-10">
            {t("outOfCreditsOverlay.title")}
          </h2>
          <p className="mt-2 text-gray-700">
            {t("outOfCreditsOverlay.subtitle")}
          </p>
        </div>

        {customerSessionClientSecret && (
          <div className="px-8 pb-6 flex-1 min-h-0">
            {/* @ts-expect-error - Stripe pricing table is a custom web component */}
            <stripe-pricing-table
              pricing-table-id={import.meta.env.VITE_STRIPE_PRICING_TABLE_ID}
              publishable-key={import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY}
              customer-session-client-secret={customerSessionClientSecret}
            />
          </div>
        )}

        {!customerSessionClientSecret &&
          (userData?.status === UserStatus.TRIAL ||
            userData?.status === UserStatus.CANCELED) && (
            <div className="px-8 pb-6 text-sm text-gray-500">
              {t("common.loading")}
            </div>
          )}

        <div className="px-8 pb-8 pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-700">
            {t("outOfCreditsOverlay.feedbackPrompt")}{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-blue-600 hover:text-blue-500 font-medium underline"
            >
              {t("outOfCreditsOverlay.writeUs")}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
